<template>
  <div :class="['container', isCreateTask ? '' : 'is-update-task']">
    <h5 class="header-title">
      <div>
        The report for task
        <b>{{ taskId }}</b>
        :
      </div>
      <div class="buttons-wrapper">
        <ts-button
          v-if="reportModified"
          :busy="loading"
          type="primary"
          @button-click="refreshReport"
          :disabled="
            reportStats.succeeded + reportStats.failed === reportModified.length
          "
        >
          Refresh the report
        </ts-button>
        <ts-button
          :busy="loading"
          type="secondary"
          @button-click="downloadReport"
        >
          Download Report
        </ts-button>
        <a
          :href="fileData"
          :download="`report_of_task_${taskId}.json`"
          ref="downloadLink"
        ></a>
        <ts-button
          v-if="showViewCompanyButton"
          type="primary"
          @button-click="viewCompany"
        >
          View Company
        </ts-button>
      </div>
    </h5>
    <hr />
    <div>
      <ts-spinner
        :data-visible="loading || null"
        data-size="medium"
        data-message="Loading the report..."
      />
      <template v-if="!loading && reportData">
        <div class="row">
          <div class="col-sm-8">
            <EdifierReportProgress
              v-if="reportModified"
              :total="reportModified.length"
              :report-stats="reportStats"
            />
          </div>
          <div class="col-sm-4">
            <EdifierReportFilter
              :report-stats="reportStats"
              :existing-fragment-types="existingFragmentTypes"
              @filter-toggle="onFilterToggle"
            />
            <span v-if="hasFilter">
              Filtered report results count: {{ filteredReport.length }}
            </span>
          </div>
        </div>

        <template
          v-if="
            createdCompanyData && Object.keys(createdCompanyData).length > 0
          "
        >
          <h5>Created Company Data:</h5>
          <div class="wrapper">
            <vue-json-pretty
              :data="createdCompanyData"
              :deep="showFullCreatedBranchesData ? 3 : 1"
              show-length
              show-line-number
              show-icon
              virtual
              :show-double-quotes="false"
            />
          </div>
          <hr />
        </template>
        <div
          v-else-if="
            isCreateTask && reportStats['in progress'] !== reportModified.length
          "
          class="alert alert-danger"
          role="alert"
        >
          No company/branch was created due to an error, see the below report:
        </div>

        <template v-if="reportModified">
          <h5>Fragments report:</h5>
          <div class="wrapper">
            <vue-json-pretty
              :data="filteredReport"
              :deep="showFullReport ? 5 : 1"
              show-length
              show-line-number
              show-icon
              virtual
              :show-double-quotes="false"
            >
              <template #nodeValue="{ node, defaultValue }">
                <template v-if="node.key === 'status'">
                  <span
                    :class="`badge badge-${getStatusBadgeClassName(
                      node.content.toLowerCase()
                    )}`"
                  >
                    {{ node.content }}
                  </span>
                </template>
                <template v-else>{{ defaultValue }}</template>
              </template>
            </vue-json-pretty>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';
import '@tradeshift/elements.spinner';
import '@tradeshift/elements.button';
import '@tradeshift/elements.status';
import EdifierReportFilter from './EdifierReportFilter';
import EdifierReportProgress from './EdifierReportProgress';
import { getEdifierTaskReport } from '../../api';

const COLLAPSE_JSON_THRESHOLD = 200;

export default {
  components: {
    VueJsonPretty,
    EdifierReportProgress,
    EdifierReportFilter,
  },
  props: {
    taskId: {
      type: String,
      required: true,
      default: null,
    },
    isCreateTask: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      reportData: null,
      report: null,
      reportStats: {
        succeeded: 0,
        failed: 0,
        'in progress': 0,
      },
      statusTagTypes: {
        succeeded: 'success',
        failed: 'danger',
        'in progress': 'primary',
      },
      filterTags: {
        statuses: [],
        fragmentTypes: [],
      },
      existingFragmentTypes: new Set(),
    };
  },
  computed: {
    reportModified() {
      return this.report || [];
    },
    createdCompanyData() {
      const companyData = { ...this.reportData?.companyData };
      // TODO: temp - it should get removed after Edifier removes the password from the response
      if (companyData) {
        delete companyData.password;
      }
      if (companyData.branches && companyData.branches.length > 0) {
        companyData.branches.forEach((branch) => delete branch.password);
      }

      return companyData;
    },
    showViewCompanyButton() {
      const hasCompanyId =
        this.createdCompanyData && this.createdCompanyData?.companyId;
      return hasCompanyId && this.isCreateTask;
    },
    showFullCreatedBranchesData() {
      return (
        this.createdCompanyData?.branches?.length < COLLAPSE_JSON_THRESHOLD
      );
    },
    showFullReport() {
      return this.filteredReport?.length < COLLAPSE_JSON_THRESHOLD;
    },
    fileData() {
      const data = JSON.stringify(this.reportData, null, 4);
      return `data:text/json;charset=utf-8,${encodeURIComponent(data)}`;
    },
    hasFilter() {
      const { statuses, fragmentTypes } = this.filterTags;
      const hasStatusFilter = statuses.length > 0;
      const hasFragTypeFilter = fragmentTypes.length > 0;
      return hasStatusFilter || hasFragTypeFilter;
    },
    filteredReport() {
      const { statuses, fragmentTypes } = this.filterTags;
      const hasStatusFilter = statuses.length > 0;
      const hasFragTypeFilter = fragmentTypes.length > 0;
      if (!this.hasFilter) {
        return this.reportModified;
      }
      return this.reportModified.filter((item) => {
        let statusMatch = false;
        let fragmentTypeMatch = false;
        if (statuses.includes(item.status)) {
          statusMatch = true;
        }
        if (fragmentTypes.includes(item.fragmentType)) {
          fragmentTypeMatch = true;
        }
        if (hasStatusFilter && hasFragTypeFilter) {
          return statusMatch && fragmentTypeMatch;
        }
        return statusMatch || fragmentTypeMatch;
      });
    },
  },
  watch: {
    taskId(val) {
      this.getReportData(val);
    },
  },
  mounted() {
    this.getReportData(this.taskId);
  },
  methods: {
    onFilterToggle({ value, category }) {
      // update the filter tags
      const index = this.filterTags[category].indexOf(value);
      if (index > -1) {
        this.filterTags[category].splice(index, 1);
      } else {
        this.filterTags[category].push(value);
      }
    },
    getReportData(taskId) {
      this.loading = true;
      getEdifierTaskReport(taskId, this.isCreateTask)
        .then((reportData) => {
          this.reportStats = {
            succeeded: 0,
            failed: 0,
            'in progress': 0,
          };

          // process report data
          const reports = _.cloneDeep(reportData?.report || []);
          if (reports && reports.length > 0) {
            reports.forEach((item) => {
              const currentCount = this.reportStats[item.status.toLowerCase()];
              this.reportStats[item.status.toLowerCase()] =
                currentCount === undefined ? 1 : currentCount + 1;
              this.existingFragmentTypes.add(item.fragmentType);
              delete item.taskId;
              delete item.rootId;
              if (this.isCreateTask) {
                delete item.action;
                delete item.from;
              }
            });
          }

          this.report = reports;
          this.reportData = reportData;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    refreshReport() {
      this.getReportData(this.taskId);
    },
    getStatusBadgeClassName(status) {
      return this.statusTagTypes[status];
    },
    viewCompany() {
      const companyId = this.createdCompanyData?.companyId;
      window.location.assign(`/support/tenant/${companyId}`);
    },
    downloadReport() {
      this.$refs.downloadLink.click();
    },
  },
};
</script>

<style scoped>
.header-title {
  padding-top: 1em;
}

.buttons-wrapper {
  margin-top: 1em;
}

.buttons-wrapper > ts-button {
  margin-right: 0.5em;
}

.wrapper {
  border: 1px solid #cccccc;
  border-radius: 2px;
  background-color: #f5f5f5;
  overflow: auto;
  /* full view width - main nav panel - outer margin main container - inner padding of tab content - border */
  max-width: calc(100vw - 320px - 60px - 60px - 2px);
}

.is-update-task .wrapper {
  /* full view width - main nav panel - overview side panel - outer margin main container - inner padding of tab content - border */
  max-width: calc(100vw - 320px - 320px - 60px - 60px - 2px);
}

.status-badge {
  text-transform: capitalize;
}

.badge {
  font-size: 12px;
}
</style>

<style>
.vjs-key {
  color: #595959;
}

.vjs-value-string {
  color: #031aa1;
}
</style>
